import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import { AavOrders } from '../../store/slices';
import { IStore } from '../../store/types';
import AavOrdersFilters from '../../components/AavOrdersFilters';
import AavOrdersTable from '../../components/AavOrdersTable';

const DEFAULT_PARAMS = {
    page: 1,
    limit: 50,
    sortDirection: 'DESC',
    statusId: null,
    search: null,
    tags: null,
    createdAtMin: null,
    createdAtMax: null,
};

/** Read-only list of the AAV Shopify orders synced into the AAV backend (`/aav/orders`). */
const AavOrdersPage = () => {
    const dispatch = useDispatch();
    const { list, status, queryParams } = useSelector<IStore, any>(
        (state) => state.aavOrders || { list: [], status: null, queryParams: DEFAULT_PARAMS }
    );
    // Bumped on "Clear filters" so the uncontrolled fields remount empty.
    const [filtersKey, setFiltersKey] = useState(0);

    const loadOrders = (params: any) => {
        dispatch<any>(AavOrders.getAavOrders(params));
    };

    const clearFilters = () => {
        dispatch(AavOrders.clearAavOrdersFilters());
        setFiltersKey((key) => key + 1);
        loadOrders({ ...DEFAULT_PARAMS });
    };

    useEffect(() => {
        loadOrders({ ...DEFAULT_PARAMS, ...queryParams });
        // Mount-only load, as before; the filters and pagination reload on their own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentParams = { ...DEFAULT_PARAMS, ...queryParams };
    const loading = status === 'loading';
    const rows: any[] = Array.isArray(list) ? list : [];

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Production"
                title="AAV orders"
                badges={currentParams.total > 0 ? <StatusChip tone="neutral">{Number(currentParams.total).toLocaleString('en-US')} total</StatusChip> : undefined}
            />
            <Panel padding="none">
                <AavOrdersFilters key={filtersKey} queryParams={currentParams} loadOrders={loadOrders} loading={loading} onClear={clearFilters} />
                {loading && (
                    <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading orders">
                        {[0, 1, 2, 3, 4, 5].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
                    </div>
                )}
                {!loading && rows.length > 0 && <AavOrdersTable orders={rows} paging={currentParams} pagingUpdate={loadOrders} />}
                {status === 'rejected' && rows.length === 0 && (
                    <EmptyState
                        title="Orders could not be loaded"
                        hint="The AAV service did not answer. Try again in a moment."
                        action={<Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => loadOrders(currentParams)}>Retry</Button>}
                    />
                )}
                {status === 'resolved' && rows.length === 0 && (
                    <EmptyState
                        title="No orders match these filters"
                        hint="Try another status, a wider date range or fewer tags."
                        action={<Button variant="outlined" size="small" onClick={clearFilters}>Clear filters</Button>}
                    />
                )}
            </Panel>
        </PageContainer>
    );
};

export default AavOrdersPage;
